import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { BASE_PAYLOAD } from '@routes/sidebar/admin-center/___shared___/constants.ts';
import type {
    AssessorTaskRequest,
    AssessorTaskResponse,
} from '@routes/sidebar/admin-center/assessor/___pages___/tasks/api/assessorTasks.api.ts';
import {
    searchAssessorHistory,
    searchAssessorQueue,
} from '@routes/sidebar/admin-center/assessor/___pages___/tasks/api/assessorTasks.api.ts';
import {
    ASSESSOR_SEARCH_FILTERS,
    ASSESSOR_TAB_DEFAULT_PAGE_NUMBER,
    ASSESSOR_TAB_DEFAULT_PAGE_SIZE,
} from '@routes/sidebar/admin-center/assessor/___shared___/constants.ts';
import { extractError } from '@utils/slice.ts';

interface AssessorTasksError {
    message?: string;
    [key: string]: unknown;
}

interface AssessorTasksState {
    error: AssessorTasksError | null;
    tasksToAssess: AssessorTaskResponse;
    tasksToExempt: AssessorTaskResponse;
    history: AssessorTaskResponse;
    tasksToAssessRequestData: AssessorTaskRequest;
    tasksToExemptRequestData: AssessorTaskRequest;
    historyRequestData: AssessorTaskRequest;
    isGetTasksToAssessPending: boolean;
    isGetTasksToExemptPending: boolean;
    isGetHistoryPending: boolean;
}

const INITIAL_PAGE: AssessorTaskResponse = {
    data: [],
    pagination: {
        maxPageSize: ASSESSOR_TAB_DEFAULT_PAGE_SIZE,
        pageNumber: ASSESSOR_TAB_DEFAULT_PAGE_NUMBER,
        pageSize: ASSESSOR_TAB_DEFAULT_PAGE_SIZE,
        totalCount: 0,
        totalPages: 0,
    },
    resultsDescription: null,
};

const createInitialRequest = (isExemption: boolean | null): AssessorTaskRequest => ({
    ...BASE_PAYLOAD,
    pageNumber: ASSESSOR_TAB_DEFAULT_PAGE_NUMBER,
    pageSize: ASSESSOR_TAB_DEFAULT_PAGE_SIZE,
    filters:
        isExemption === null
            ? null
            : {
                  [ASSESSOR_SEARCH_FILTERS.IS_EXEMPTION]: [String(isExemption)],
              },
});

const initialState: AssessorTasksState = {
    error: null,
    tasksToAssess: INITIAL_PAGE,
    tasksToExempt: INITIAL_PAGE,
    history: INITIAL_PAGE,
    tasksToAssessRequestData: createInitialRequest(false),
    tasksToExemptRequestData: createInitialRequest(true),
    historyRequestData: createInitialRequest(null),
    isGetTasksToAssessPending: false,
    isGetTasksToExemptPending: false,
    isGetHistoryPending: false,
};

export const getTasksToAssess = createAsyncThunk<
    AssessorTaskResponse,
    { payload: AssessorTaskRequest },
    { rejectValue: AssessorTasksError }
>('assessorTasks/getTasksToAssess', ({ payload }, { rejectWithValue }) =>
    searchAssessorQueue(payload)
        .then((res) => res)
        .catch((err) => rejectWithValue(err)),
);

export const getTasksToExempt = createAsyncThunk<
    AssessorTaskResponse,
    { payload: AssessorTaskRequest },
    { rejectValue: AssessorTasksError }
>('assessorTasks/getTasksToExempt', ({ payload }, { rejectWithValue }) =>
    searchAssessorQueue(payload)
        .then((res) => res)
        .catch((err) => rejectWithValue(err)),
);

export const getAssessorHistory = createAsyncThunk<
    AssessorTaskResponse,
    { payload: AssessorTaskRequest },
    { rejectValue: AssessorTasksError }
>('assessorTasks/getAssessorHistory', ({ payload }, { rejectWithValue }) =>
    searchAssessorHistory(payload)
        .then((res) => res)
        .catch((err) => rejectWithValue(err)),
);

const assessorTasksSlice = createSlice({
    name: 'assessorTasks',
    initialState,
    reducers: {
        clearAssessorTasksState: () => initialState,
    },
    extraReducers: (builder) => {
        builder
            .addCase(getTasksToAssess.pending, (state, action) => {
                state.isGetTasksToAssessPending = true;
                state.error = null;
                state.tasksToAssessRequestData = action.meta.arg.payload;
            })
            .addCase(getTasksToAssess.fulfilled, (state, action) => {
                state.isGetTasksToAssessPending = false;
                state.tasksToAssess = action.payload;
            })
            .addCase(getTasksToAssess.rejected, (state, action) => {
                state.isGetTasksToAssessPending = false;
                state.error = extractError(action.payload);
            })
            .addCase(getTasksToExempt.pending, (state, action) => {
                state.isGetTasksToExemptPending = true;
                state.error = null;
                state.tasksToExemptRequestData = action.meta.arg.payload;
            })
            .addCase(getTasksToExempt.fulfilled, (state, action) => {
                state.isGetTasksToExemptPending = false;
                state.tasksToExempt = action.payload;
            })
            .addCase(getTasksToExempt.rejected, (state, action) => {
                state.isGetTasksToExemptPending = false;
                state.error = extractError(action.payload);
            })
            .addCase(getAssessorHistory.pending, (state, action) => {
                state.isGetHistoryPending = true;
                state.error = null;
                state.historyRequestData = action.meta.arg.payload;
            })
            .addCase(getAssessorHistory.fulfilled, (state, action) => {
                state.isGetHistoryPending = false;
                state.history = action.payload;
            })
            .addCase(getAssessorHistory.rejected, (state, action) => {
                state.isGetHistoryPending = false;
                state.error = extractError(action.payload);
            });
    },
});

export const { clearAssessorTasksState } = assessorTasksSlice.actions;

export default assessorTasksSlice.reducer;
